import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors, ValidatorFn,
  Validators,
} from "@angular/forms";
import { MatNativeDateModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { format } from "date-fns";
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzSelectModule } from 'ng-zorro-antd/select';
import * as uuid from "uuid";
import { AutocompleteInputComponent } from "../autocomplete-input/autocomplete-input.component";
import { ZorroTableComponent } from "../custom-table/zorro-table.component";
import { DropdownWithSectionsComponent } from "../dropdown-with-sections/dropdown-with-sections.component";
import { FileUploadComponent } from "../file-upload/file-upload.component";
import { MenuDropdownComponent } from "../menu-dropdown/menu-dropdown.component";
import { MaterialModule } from "../../../core/shared/material.module";

// Custom validators for data types
function integerValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    const value = control.value.toString().trim();
    const isValid = /^-?\d+$/.test(value);
    return isValid ? null : { invalidInteger: { value: control.value } };
  };
}

function doubleValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    const value = control.value.toString().trim();
    const isValid = /^-?\d+(\.\d+)?$/.test(value);
    return isValid ? null : { invalidDouble: { value: control.value } };
  };
}

function booleanValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) return null;
    const value = control.value.toString().trim().toLowerCase();
    const isValid = value === 'true' || value === 'false';
    return isValid ? null : { invalidBoolean: { value: control.value } };
  };
}

/** Default Key / Value columns used by `table` and `table-input` fields. */
const DEFAULT_KEY_VALUE_TABLE_COLUMNS: any[] = [
  {
    display_name: "Key",
    key: "key",
    headerStyles: {
      background: "#7593F2",
      width: "35%",
      color: "#fff",
    },
    _editConfig: {
      type: "input",
      placeholder: "Add key",
      isAutoCompleteEnable: true,
      autocompleteKey: "key",
    },
  },
  {
    display_name: "Value",
    key: "value",
    headerStyles: {
      background: "#7593F2",
      width: "40%",
      color: "#fff",
    },
    _editConfig: {
      type: "input",
      placeholder: "Add value",
    },
  },
  {
    key: "edit",
    display_name: "",
    cellStyles: { "font-size": "12px" },
    headerStyles: {
      background: "#7593F2",
      "font-weight": "600",
      width: "5%",
      color: "#fff",
    },
    _config: {
      type: "svg-editor",
      value: "../../../../assets/images/virtusa-icons/edit-light.svg",
      isClickable: "true",
      actionType: "edit",
    },
    _editConfig: {
      type: "svg-editor",
      actionType: "add",
      value: "../../../../assets/images/virtusa-icons/tick.svg",
      isClickable: "true",
    },
  },
  {
    key: "delete",
    display_name: "",
    cellStyles: { "font-size": "12px" },
    headerStyles: {
      background: "#7593F2",
      borderRadius: "0 6px 0 0",
      "font-weight": "600",
      width: "5%",
      color: "#fff",
    },
    _config: {
      type: "svg-editor",
      value: "../../../../assets/images/virtusa-icons/delete-light.svg",
      isClickable: "true",
      actionType: "delete",
    },
    _editConfig: {
      type: "svg-editor",
      value: "../../../../assets/images/virtusa-icons/cross.svg",
      isClickable: "true",
      actionType: "cancel",
    },
  },
];

@Component({
  selector: "app-dynamic-form",
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    AutocompleteInputComponent,
    FileUploadComponent,
    NzDatePickerModule,
    DropdownWithSectionsComponent,
    ZorroTableComponent,
    MenuDropdownComponent,
    MaterialModule,
    NzSelectModule,
  ],
  templateUrl: "./dynamic-form.component.html",
  styleUrl: "./dynamic-form.component.scss",
})
export class DynamicFormComponent implements OnInit, OnChanges {
  [x: string]: any;
  @Input()
  form: FormGroup = new FormGroup({});

  @Input()
  formFields: any = [];

  @Output() modelChange = new EventEmitter();
  selectedDate: Date = new Date();

  date: any;

  showPassword: { [key: string]: boolean } = {};
  maskedFields: { [key: string]: boolean } = {};

  tableData: { [key: string]: { columns: any[]; rows: any[] } } = {};
  clonedData: { [key: string]: { [rowId: string]: any } } = {};
  autocompleteOptionsByKey: any = {};

  // Custom validator to allow only letters, numbers, and underscores
  alphanumericUnderscoreValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      const regex = /^[a-zA-Z0-9_]*$/;
      if (control.value && !regex.test(control.value)) {
        return { invalidName: { value: control.value } };
      }
      return null;
    };
  }

  onAutoCompleteChange(value: string, field: any, referId = false): void {
    this.form.get(referId ? field.id : field.key)?.setValue(value);
  }

  onAutoCompleteChangeParam(value: string, field: any, param: any): void {
    field.uiParams[param.key] = value;
  }

  ngOnInit() {
    this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["formFields"]) {
      this.buildForm(true);
    }
  }

  buildForm(isFormChange: boolean = false) {
    // Preserve existing values when rebuilding form
    const existingValues: any = {};
    if (isFormChange) {
      Object.keys(this.form.controls).forEach((key) => {
        existingValues[key] = this.form.controls[key].value;
        this.form.removeControl(key);
      });
    }

    // Add controls for each field
    this.formFields?.forEach((field: any) => {
      if (field.type === "input-list") return;
      if (this.isTableField(field)) {
        this.setupTable(field);
        return;
      }
      // Use existing value if available, otherwise use field.value
      const initialValue = this.resolveFieldInitialValue(
        field,
        isFormChange && existingValues.hasOwnProperty(field.key)
          ? existingValues[field.key]
          : field.value
      );
      const control = new FormControl(initialValue, []);

      if (
        (field.name === "Reusable Components Folder Name" ||
        field.name === "Reusable Component Name" ||
        field.name === "Application Name" ||
        field.name === "Page Name") &&
        field.type !== "select"
      ) {
        control.addValidators(this.alphanumericUnderscoreValidator());
      }

      if (field.isDisabled || field.disabled) {
        control.disable();
      }
      if (field.validations?.required) {
        control.addValidators(Validators.required);
      }

      this.form.addControl(field.key, control);
    });

    // Setup dynamic visibility
    this.formFields.forEach((field: any) => {
      if (field.visibleIf) {
        this.form.get(field.visibleIf.key)?.valueChanges.subscribe((value) => {
          if (this.matchesVisibleIf(field.visibleIf, value)) {
            this.form.controls[field.key]?.setValidators(
              field.validations?.required ? Validators.required : []
            );
            if (this.isDisabledCheckbox(field)) {
              this.form.controls[field.key]?.setValue(true);
            }
          } else {
            this.form.controls[field.key]?.clearValidators();
            if (!this.isDisabledCheckbox(field)) {
              this.form.controls[field.key]?.setValue("");
            }
          }
          this.form.controls[field.key]?.updateValueAndValidity();
        });
      }

      // Setup dynamic disabled state
      if (field.disabledIf) {
        this.form.get(field.disabledIf.key)?.valueChanges.subscribe((value) => {
          const control = this.form.get(field.key);
          if (control) {
            if (value === field.disabledIf.value) {
              control.disable();
            } else {
              control.enable();
            }
          }
        });

        // Set initial disabled state
        const initialValue = this.form.get(field.disabledIf.key)?.value;
        if (initialValue === field.disabledIf.value) {
          this.form.get(field.key)?.disable();
        }
      }
    });

    // Setup min-date constraints (e.g. end date must be after start date)
    this.formFields.forEach((field: any) => {
      if (field.linkedMinDateKey) {
        this.form.get(field.linkedMinDateKey)?.valueChanges.subscribe((minVal: any) => {
          if (minVal) {
            const endVal = this.form.get(field.key)?.value;
            if (endVal) {
              const start = new Date(minVal);
              const end = new Date(endVal);
              start.setHours(0, 0, 0, 0);
              end.setHours(0, 0, 0, 0);
              if (end < start) {
                this.form.get(field.key)?.setValue(null);
              }
            }
          }
        });
      }
    });

    // Setup dataType-based validation (only if field has validateByDataType flag)
    this.formFields.forEach((field: any) => {
      if (field.validateByDataType && field.key === 'value') {
        const dataTypeControl = this.form.get('dataType');
        const valueControl = this.form.get('value');
        
        if (dataTypeControl && valueControl) {
          dataTypeControl.valueChanges.subscribe((dataType: string) => {
            // Clear existing validators
            valueControl.clearValidators();
            
            // Add validators based on dataType
            if (dataType === 'Integer') {
              valueControl.setValidators([integerValidator()]);
            } else if (dataType === 'Double') {
              valueControl.setValidators([doubleValidator()]);
            } else if (dataType === 'Boolean') {
              valueControl.setValidators([booleanValidator()]);
            }
            
            valueControl.updateValueAndValidity();
          });
          
          // Set initial validation based on current dataType value
          const currentDataType = dataTypeControl.value;
          if (currentDataType === 'Integer') {
            valueControl.setValidators([integerValidator()]);
          } else if (currentDataType === 'Double') {
            valueControl.setValidators([doubleValidator()]);
          } else if (currentDataType === 'Boolean') {
            valueControl.setValidators([booleanValidator()]);
          }
          valueControl.updateValueAndValidity();
        }
      }
    });

    // Initialize masked fields (password fields with existing values in edit mode)
    this.maskedFields = {};
    this.formFields?.forEach((field: any) => {
      if (field.isMasked) {
        this.maskedFields[field.key] = true;
      }
    });
  }

  unmaskField(key: string) {
    this.maskedFields[key] = false;
    this.form.get(key)?.setValue('');
  }

  addControl(field: any) {
    this.form.addControl(field.key, new FormControl(field.value, []));
    if (field.isDisabled) this.form.controls[field.key].disable();
    if (field.validations?.required) {
      this.form.controls[field.key].addValidators(Validators.required);
    }
  }

  setupTable(field: any) {
    const tableGroup = new FormGroup({});
    const rows: any[] = [];

    const configData = field.value || {};
    if (configData && typeof configData === "object" && !Array.isArray(configData)) {
      Object.keys(configData).forEach((key) => {
        const value = configData[key];
        tableGroup.addControl(key, new FormControl(value));
        rows.push({
          key: key,
          value: value,
          id: uuid.v4(),
        });
      });
    }
    this.autocompleteOptionsByKey['key'] = field.keys || []; // Autocomplete for key column

    if (this.form.contains(field.key)) {
      this.form.removeControl(field.key);
    }
    this.form.addControl(field.key, tableGroup);

    this.tableData[field.key] = {
      columns: field.columns?.length ? field.columns : DEFAULT_KEY_VALUE_TABLE_COLUMNS,
      rows: rows,
    };
  }

  private isTableField(field: any): boolean {
    return field?.type === "table" || field?.type === "table-input";
  }

  handleTableActions(event: any, field: any) {
    const tableControl = this.form.get(field.key) as FormGroup;
    if (!tableControl) return;

    switch (event.actionType) {
      case "add":
        this.handleTableAddAction(event, field, tableControl);
        break;
      case "edit":
        this.handleTableEditAction(event, field);
        break;
      case "cancel":
        this.handleTableCancelAction(event, field);
        break;
      case "delete":
        this.handleTableDeleteAction(event, field, tableControl);
        break;
    }
  }

  private handleTableAddAction(event: any, field: any, tableControl: FormGroup): void {
    const key = (event?.data?.row?.key || '').trim();
    if (!key) {
      this.markRowKeyError(event, field);
      return;
    }
    if (this.isNewDraftRow(event)) {
      this.saveNewDraftRow(field, tableControl);
    } else {
      this.saveEditedRow(event, field, tableControl);
    }
  }

  /** Mark the row being saved as having a missing key so the table can highlight it. */
  private markRowKeyError(event: any, field: any): void {
    const rows = this.tableData[field.key]?.rows;
    if (!rows) return;
    const rowId = event?.data?.row?.id;
    const rowIndex = rowId
      ? rows.findIndex((r: any) => r.id === rowId)
      : rows.findIndex((r: any) => r.isDraft);
    if (rowIndex > -1) {
      rows[rowIndex] = { ...rows[rowIndex], hasError: true };
      this.tableData[field.key].rows = [...rows];
    }
  }

  private isNewDraftRow(event: any): boolean {
    return event?.data?.row?.key && event?.data?.row?.isDraft;
  }

  private saveNewDraftRow(field: any, tableControl: FormGroup): void {
    const newRow = {
      ...this.tableData[field.key].rows[0],
      isRowEditable: false,
      isDraft: false,
      hasError: false,
      id: uuid.v4(),
    };

    this.removeTempControlAndAddNew(field, tableControl, newRow);
    this.updateTableRowsWithNewRow(field, newRow);
  }

  private removeTempControlAndAddNew(field: any, tableControl: FormGroup, newRow: any): void {
    const tempKey = 'new_key_' + this.tableData[field.key].rows.length;
    if (tableControl.get(tempKey)) {
      tableControl.removeControl(tempKey);
    }
    tableControl.addControl(newRow.key, new FormControl(newRow.value));
  }

  private updateTableRowsWithNewRow(field: any, newRow: any): void {
    this.tableData[field.key].rows.shift();
    this.tableData[field.key].rows = [newRow, ...this.tableData[field.key].rows];
  }

  private saveEditedRow(event: any, field: any, tableControl: FormGroup): void {
    const editedRow = event.data.row;
    const originalRow = this.clonedData[field.key]?.[editedRow.id];

    this.updateTableControlForEditedRow(tableControl, editedRow, originalRow);
    this.updateTableRowData(field, editedRow);
    this.cleanupClonedData(field, editedRow.id);
  }

  private updateTableControlForEditedRow(tableControl: FormGroup, editedRow: any, originalRow: any): void {
    if (this.hasKeyChanged(originalRow, editedRow)) {
      tableControl.removeControl(originalRow.key);
      tableControl.addControl(editedRow.key, new FormControl(editedRow.value));
    } else if (tableControl.get(editedRow.key)) {
      tableControl.get(editedRow.key)?.setValue(editedRow.value);
    }
  }

  private hasKeyChanged(originalRow: any, editedRow: any): boolean {
    return originalRow && originalRow.key !== editedRow.key;
  }

  private updateTableRowData(field: any, editedRow: any): void {
    const rowIndex = this.tableData[field.key].rows.findIndex((r: any) => r.id === editedRow.id);
    if (rowIndex > -1) {
      this.tableData[field.key].rows[rowIndex] = {
        ...editedRow,
        isRowEditable: false,
        isDraft: false,
        hasError: false,
      };
      this.tableData[field.key].rows = [...this.tableData[field.key].rows];
    }
  }

  private cleanupClonedData(field: any, rowId: string): void {
    delete this.clonedData[field.key]?.[rowId];
  }

  private handleTableEditAction(event: any, field: any): void {
    if (!event?.data?.row?.id) return;

    this.storeRowClone(field, event.data.row);
    this.markRowAsEditable(field, event.data.row.id);
  }

  private storeRowClone(field: any, row: any): void {
    if (!this.clonedData[field.key]) {
      this.clonedData[field.key] = {};
    }
    this.clonedData[field.key][row.id] = { ...row };
  }

  private markRowAsEditable(field: any, rowId: string): void {
    this.tableData[field.key].rows = this.tableData[field.key].rows.map((row: any) => {
      if (row.id === rowId) {
        return { ...row, isRowEditable: true };
      }
      return row;
    });
  }

  private handleTableCancelAction(event: any, field: any): void {
    if (event?.data?.row?.isDraft) {
      this.cancelDraftRow(field);
    } else {
      this.revertEditedRow(event, field);
    }
  }

  private cancelDraftRow(field: any): void {
    this.tableData[field.key].rows.shift();
    this.tableData[field.key].rows = [...this.tableData[field.key].rows];
  }

  private revertEditedRow(event: any, field: any): void {
    const originalRow = this.clonedData[field.key]?.[event.data.row.id];
    if (!originalRow) return;

    const rowIndex = this.tableData[field.key].rows.findIndex((r: any) => r.id === event.data.row.id);
    if (rowIndex > -1) {
      this.tableData[field.key].rows[rowIndex] = { 
        ...originalRow, 
        isRowEditable: false, 
        isDraft: false 
      };
    }
    
    this.cleanupClonedData(field, event.data.row.id);
    this.tableData[field.key].rows = [...this.tableData[field.key].rows];
  }

  private handleTableDeleteAction(event: any, field: any, tableControl: FormGroup): void {
    if (!event?.data?.row?.id) return;

    const rowIndex = this.findRowIndex(field, event.data.row.id);
    if (rowIndex > -1) {
      tableControl.removeControl(event.data.row.key);
      this.tableData[field.key].rows.splice(rowIndex, 1);
    }
  }

  private findRowIndex(field: any, rowId: string): number {
    return this.tableData[field.key].rows.findIndex((r: any) => r.id === rowId);
  }

  /** Returns true when the table for the given field already has an unsaved draft row. */
  hasDraftRow(field: any): boolean {
    return this.tableData[field.key]?.rows?.some((row: any) => row.isDraft) ?? false;
  }

  addNew(field: any) {
    const tableControl = this.form.get(field.key) as FormGroup;
    if (!tableControl) return;

    // Do not allow adding a new row while an unsaved draft row is still present
    if (this.hasDraftRow(field)) return;

    const obj = {
      key: "",
      type: "text",
      isRowEditable: true,
      isDraft: true,
    };
    this.tableData[field.key].rows = [obj, ...this.tableData[field.key].rows];
    tableControl.addControl('new_key_' + this.tableData[field.key].rows.length, new FormControl(''));
  }

  isFieldDisabled(field: any): boolean {
    if (!field.disabledIf) return false;

    return this.form.get(field.disabledIf.key)?.value === field.disabledIf.value;
  }

  private isDisabledCheckbox(field: any): boolean {
    return field.type === 'checkbox' && (field.disabled || field.isDisabled);
  }

  private resolveFieldInitialValue(field: any, current?: any): any {
    if (this.isDisabledCheckbox(field)) {
      return field.value ?? true;
    }
    if (field.type === 'checkbox') {
      return current ?? field.value ?? false;
    }
    // Multi-select must start as an array so Angular validators work correctly
    if (field.type === 'multi-select') {
      return current ?? field.value ?? [];
    }
    return current ?? field.value;
  }

  /** Supports visibleIf.value (single) or visibleIf.values (multiple allowed). */
  matchesVisibleIf(visibleIf: any, currentValue: any): boolean {
    if (!visibleIf) return true;
    if (Array.isArray(visibleIf.values)) {
      return visibleIf.values.includes(currentValue);
    }
    return currentValue === visibleIf.value;
  }

  isFieldVisible(field: any): boolean {
    if (!field.visibleIf) return true;

    const isVisible = this.matchesVisibleIf(
      field.visibleIf,
      this.form.get(field.visibleIf.key)?.value
    );
    const control = this.form.get(field.key);
    if (control) {
      if (isVisible) {
        control.setValidators(field.validations?.required ? [Validators.required] : []);
        if (this.isDisabledCheckbox(field)) {
          control.setValue(true, { emitEvent: false });
        }
      } else {
        if (this.isTableField(field)) {
          this.form.removeControl(field.key);
        } else {
          control.clearValidators();
          control.setErrors(null);
          if (!this.isDisabledCheckbox(field)) {
            control.setValue(null, { emitEvent: false });
          }
        }
      }
      control.updateValueAndValidity(); // Recalculate validity
    } else if (isVisible && this.isTableField(field)) {
      this.setupTable(field);
    }

    return isVisible;
  }

  getValidationError(fieldKey: string): string {
    const control = this.form.get(fieldKey);
    if (!control?.errors || !control?.touched) return '';
    
    if (control.errors['invalidInteger']) {
      return 'Please enter a valid integer (e.g., 123, -456)';
    }
    if (control.errors['invalidDouble']) {
      return 'Please enter a valid number (e.g., 3.14, -0.5, 42)';
    }
    if (control.errors['invalidBoolean']) {
      return 'Please enter "true" or "false"';
    }
    return '';
  }

  isError(key: string): string | null {
    const control = this.form.controls[key];
    const errors = control?.errors;

    if (!errors) return null;

    for (const errorKey in errors) {
      switch (errorKey) {
        case "required":
          return "This field is required!";
        case "invalidName":
          return "Please remove spaces or special characters.";
        default:
          return "Invalid input.";
      }
    }

    return null;
  }

  onSelect(option:any, field: any){
    let value = option?.value;
    this.form.controls[field.key].setValue(value);
    this.modelChange.emit({ field, value });
  }

  onMenuDropdownSelect(option: any, field: any) {
    let value = option?.id || option?.value;
    this.form.controls[field.key].setValue(value);
    this.modelChange.emit({ field, value });
  }

  onSelectChange(field: any) {
    let value = this.form.controls[field.key].value;
    this.modelChange.emit({ field, value });
  }

  onSelectCard(field: any, option: any) {
  this.form.controls[field.key].setValue(option.value);
  this.modelChange.emit({
    field,
    value: option.value
  });
}


  getDisabledDateFn(field: any): (current: Date) => boolean {
    return (current: Date) => {
      const d = new Date(current);
      d.setHours(0, 0, 0, 0);

      if (field.disableFutureDates) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (d > today) return true;
      }

      if (field.linkedMinDateKey) {
        const minVal = this.form.get(field.linkedMinDateKey)?.value;
        if (minVal) {
          const min = new Date(minVal);
          min.setHours(0, 0, 0, 0);
          if (d < min) return true;
        }
      }

      return false;
    };
  }

  getFormattedDate(date: any): string {
    return date ? format(new Date(date), "yyyy-MM-dd") : "";
  }

  handleFilesSelected(event: any) {
    return event?.files[0];
  }

  togglePassword(key: string): void {
    this.showPassword[key] = !this.showPassword[key];
  }

}
